// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import { Body, Button, Card, LgPadding, Toast, ToastContext } from '@alto/design-system';
import { type Date, DatePicker, useFetchHomescreen, useInvalidateMedDetails } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import { ProgynyRetrobillRequestsEndpoint } from '@alto/scriptdash/alto/patient_app/progyny_retrobill_requests/v1';
import React, { useContext, useState } from 'react';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { apiEndpointHandler } from '~shared/helpers/api';
import { useInvalidatePrescriptions } from '~shared/queries';
import { useSelectorShared } from '~shared/store';
import { ProgynyPage } from '../../shared/components/ProgynyPage';

export const OrderEarlySubmit = () => {
  const toast = useContext(ToastContext);
  const { getParam, goBack } = useNavigation<'RouteOrderEarlySubmit'>();
  const { invalidatePrescriptionsQuery } = useInvalidatePrescriptions();
  const { invalidateMedicationQuery, invalidatePrescriptionsQuery: invalidateMedDetailsPrescriptionsQuery } =
    useInvalidateMedDetails();
  const currentUserID = useSelectorShared(getCurrentUserID);
  const { refetch: refetchHomescreen } = useFetchHomescreen(currentUserID);

  const employer = getParam('employer');
  const cycleType = getParam('cycleType');
  const selectedBackupPaymentMethodId = getParam('selectedBackupPaymentMethodId');
  const prescriptionId = getParam('prescriptionId');

  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const submit = () => {
    if (
      startDate === undefined ||
      employer === undefined ||
      cycleType === undefined ||
      selectedBackupPaymentMethodId === undefined ||
      prescriptionId === undefined
    )
      return;

    const onComplete = () => {
      setIsSubmitting(false);
      // This is necessary to ensure that the rxs refresh properly on the homescreen. Otherwise, the rxs will still
      // show the 'Order Early' button even after the patient submits the order-early request.
      refetchHomescreen();
      goBack();
      goBack();
    };

    const onFailure = () => {
      onComplete();
      toast.addToast(
        <Toast variant="error">
          Something went wrong. Please try again or contact support if the problem persists.
        </Toast>,
      );
    };

    const onSuccess = () => {
      onComplete();
      invalidatePrescriptionsQuery();
      invalidateMedicationQuery();
      invalidateMedDetailsPrescriptionsQuery();
      toast.addToast(<Toast variant="success">Early order request sent!</Toast>);
    };

    setIsSubmitting(true);
    ProgynyRetrobillRequestsEndpoint(apiEndpointHandler)
      .submit({
        prescription_id: prescriptionId,
        employer,
        start_date: startDate.dateString,
        cycle_type: cycleType,
        backup_payment_method_id: selectedBackupPaymentMethodId,
      })
      .then((response) => {
        // eslint-disable-next-line promise/always-return
        if (response.errors?.length) onFailure();
        else onSuccess();
      })
      .catch(onFailure);
  };

  return (
    <ProgynyPage
      dismissIcon="chevronleft"
      NavBarCenterContent="Start Date"
      onDismiss={() => {
        goBack();
      }}
      HeaderContent={
        <LgPadding topPadding={SPACING.STATIC.SM}>
          <Body>When is your earliest possible start date for your medications?</Body>
        </LgPadding>
      }
      buttons={[
        <Button
          key="submit"
          label="Request early ordering"
          onPress={submit}
          loading={isSubmitting}
          disabled={!startDate}
        />,
      ]}
    >
      <Card>
        <DatePicker
          onDayPress={setStartDate}
          markedDates={
            startDate
              ? {
                  [startDate.dateString]: {
                    selected: true,
                  },
                }
              : undefined
          }
        />
      </Card>
    </ProgynyPage>
  );
};
